import { Divider } from "@/Divider";
import { LayoutHeader } from "@/Header";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import pet1Src from "$/pet-1.svg";
import NotificationCard from "@/NotificationCard";
import DiaryHeader from "@/DiaryHeader";
import CommentItem from "@/CommentItem";
import { CalendarNew as CalendarS } from "@/CalendarNew";
import { Dialog } from "@/Dialog";
import { ImageViewer } from "@/ImageViewer";
import {
  useBusinessNoticeDetail,
  useBusinessNoticeReplySave,
  useBusinessNoticeReply,
} from "~/services/business/notice";
import { useState, useEffect, useMemo, useCallback } from "react";
import { IconCalendar } from "@/Icon/Calendar";
import { IconNotice } from "@/Icon/Notice";
import { getAttachmentMediaType } from "%/mediaType";
import DocAttachment from "@/common/DocAttachment";

/** 사업자가 직접 작성해 수정이 허용되는 알림장 유형 (그 외는 시스템 자동 생성) */
const EDITABLE_NOTICE_TYPES = [
  "NOTI_DIARY",
  "NOTI_SCHEDULE",
  "NOTI_ANNOUNCEMENT",
];

/** "2026-07-22T14:30:05" → "2026.07.22 14:30" */
const formatEditedAt = (dt: string) => {
  const d = new Date(dt);
  if (isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
};

const getMediaType = getAttachmentMediaType;

interface SearchDetailParams {
  noticeSeq?: string;
  send?: string;
}

export const Route = createFileRoute("/business/layout/notify-detail")({
  component: NotifyDetail,
  validateSearch: (search: Record<string, unknown>): SearchDetailParams => {
    return {
      noticeSeq: search.noticeSeq as string,
      send: search.send as string,
    };
  },
});

function NotifyDetail() {
  const { noticeSeq, send } = Route.useSearch();
  const { history } = useRouter();
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [replyMessage, setReplyMessage] = useState(""); // 댓글 입력 상태

  // Dialog 상태
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");
  const [isMediaViewerVisible, setIsMediaViewerVisible] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);

  // API 호출
  const {
    data: noticeDetail,
    isLoading,
    error,
  } = useBusinessNoticeDetail({
    notiRecvSeq: parseInt(noticeSeq || "0"),
  });

  // 댓글 목록 API
  const {
    data: replyList,
    isLoading: isReplyLoading,
    refetch: refetchReplies,
  } = useBusinessNoticeReply({
    notiRecvSeq: parseInt(noticeSeq || "0"),
  });

  // 댓글 저장 API
  const { mutate: saveReply, isPending: isReplySaving } =
    useBusinessNoticeReplySave();

  console.log("noticeDetail", noticeDetail);

  // 이미지 데이터 (API에서 받은 photos 사용)
  const [images, setImages] = useState<
    {
      fileSeq: number;
      fileUrl: string;
      thumbnailUrl?: string | null;
      thumbnailStatus?: string | null;
      muteYn?: string | null;
    }[]
  >([]);

  // Dialog 닫기 함수
  const handleDialogClose = () => {
    setIsDialogVisible(false);
    setDialogMessage("");
    setDialogTitle("");
  };

  // API 데이터가 로드되면 이미지 설정
  useEffect(() => {
    if (noticeDetail?.data?.photos && noticeDetail.data.photos.length > 0) {
      setImages(
        noticeDetail.data.photos.map((photo) => ({
          fileSeq: photo.fileSeq,
          fileUrl: photo.fileUrl,
          thumbnailUrl: photo.thumbnailUrl ?? null,
          thumbnailStatus: photo.thumbnailStatus ?? null,
          muteYn: photo.muteYn ?? null,
        })),
      );
    } else {
      setImages([]);
    }
  }, [noticeDetail?.data?.photos]);

  // 댓글 등록 함수
  const handleReplySubmit = () => {
    if (!replyMessage.trim()) {
      setDialogTitle("입력 오류");
      setDialogMessage("댓글을 입력해주세요.");
      setIsDialogVisible(true);
      return;
    }

    if (!noticeDetail?.data?.noticeSeq) {
      setDialogTitle("오류");
      setDialogMessage("알림 정보를 찾을 수 없습니다.");
      setIsDialogVisible(true);
      return;
    }

    saveReply(
      {
        noticeSeq: noticeDetail.data.noticeSeq,
        message: replyMessage.trim(),
      },
      {
        onSuccess: (response) => {
          console.log("댓글 저장 성공:", response);
          setReplyMessage(""); // 입력창 초기화
          // 댓글 목록 새로고침
          refetchReplies();
          // setDialogTitle("성공");
          // setDialogMessage("댓글이 성공적으로 등록되었습니다.");
          // setIsDialogVisible(true);
        },
        onError: (error) => {
          console.error("댓글 저장 실패:", error);
          setDialogTitle("오류");
          setDialogMessage("댓글 등록에 실패했습니다. 다시 시도해주세요.");
          setIsDialogVisible(true);
        },
      },
    );
  };

  useEffect(() => {
    if (
      noticeDetail?.data?.schedules &&
      noticeDetail.data.schedules.length > 0
    ) {
      // schedules는 Date[]가 아닌 객체 배열이므로 빈 배열로 설정
      setSelectedDates([]); // This line was removed as per the edit hint
    } else {
      setSelectedDates([]); // This line was removed as per the edit hint
    }
  }, [noticeDetail?.data?.schedules]);

  const mediaUrls = useMemo(() => {
    return images.map((img) => img.fileUrl);
  }, [images]);

  // ImageViewer 는 image/video 만 지원한다. 문서 첨부는 뷰어로 열지 않으므로 image 로 폴백.
  const currentMediaType = useMemo((): "image" | "video" => {
    if (mediaUrls[selectedMediaIndex]) {
      return getMediaType(mediaUrls[selectedMediaIndex]) === "video"
        ? "video"
        : "image";
    }
    return "image";
  }, [mediaUrls, selectedMediaIndex]);

  // 발송 후 수정된 경우 수정 일시 문자열. 발송 시점에는 등록/수정 시각이 같고, 수정 시에만 DT_UPDATE 가 앞선다.
  const editedAt = useMemo(() => {
    const regist = noticeDetail?.data?.dtRegist;
    const update = noticeDetail?.data?.dtUpdate;
    if (!regist || !update) return null;
    const registTime = new Date(regist).getTime();
    const updateTime = new Date(update).getTime();
    if (isNaN(registTime) || isNaN(updateTime)) return null;
    if (updateTime - registTime <= 1000) return null; // 1초 이내 차이는 발송 시점 그대로
    return formatEditedAt(update);
  }, [noticeDetail?.data?.dtRegist, noticeDetail?.data?.dtUpdate]);

  // 보낸 알림장 중 사업자가 직접 작성한 유형만 수정 가능.
  // 등·하원/예약 등 시스템 자동 생성 알림장은 수정 시 유형이 바뀌므로 진입 자체를 막는다.
  const canEdit =
    send === "Y" &&
    !!noticeDetail?.data?.noticeSeq &&
    EDITABLE_NOTICE_TYPES.includes(noticeDetail?.data?.cdNotiType ?? "");

  const handleEdit = () => {
    if (!noticeDetail?.data?.noticeSeq) return;
    history.push(
      `/business/layout/notify-saved-notify?noticeSeq=${noticeDetail.data.noticeSeq}&edit=Y`,
    );
  };

  // 공지사항·일정공지 알림장은 댓글 작성/노출 불가 (백엔드에서도 저장 차단)
  const isReplyBlocked =
    noticeDetail?.data?.cdNotiType === "NOTI_ANNOUNCEMENT" ||
    noticeDetail?.data?.cdNotiType === "NOTI_SCHEDULE";

  // 일정공지/공지사항은 특정 반려견 대상이 아니므로 헤더 썸네일을 목록과 동일한 유형 아이콘으로 표시
  const avatarIcon =
    noticeDetail?.data?.cdNotiType === "NOTI_SCHEDULE" ? (
      <IconCalendar active />
    ) : noticeDetail?.data?.cdNotiType === "NOTI_ANNOUNCEMENT" ? (
      <IconNotice active />
    ) : undefined;

  // 사업자가 발송한 알림장의 전체 수신자 목록 (다중 발송 확인용)
  const recipients = noticeDetail?.data?.recipients ?? [];

  // 같은 보호자의 반려견은 하나로 묶어 "초코, 보리 (홍길동)" 형태로 표시.
  // 읽음은 보호자 단위 — 보호자 앱이 열람 시 수신 행 1개만 READ_YN='Y' 처리하므로
  // 반려견별 읽음은 데이터상 무의미하고, 하나라도 읽음이면 그 보호자가 읽은 것이다.
  const groupedRecipients = useMemo(() => {
    const groups: Array<{
      key: string;
      userName: string | null;
      petNames: string[];
      hasRead: boolean;
    }> = [];
    const indexByKey = new Map<string, number>();
    for (const recipient of recipients) {
      const key =
        recipient.userSeq != null
          ? `u${recipient.userSeq}`
          : `p${recipient.petSeq}`; // 보호자 식별 불가 시 반려견 단위로 개별 표시
      const petName = recipient.petName || "이름 없음";
      const read = recipient.readYn === "Y";
      const idx = indexByKey.get(key);
      if (idx === undefined) {
        indexByKey.set(key, groups.length);
        groups.push({
          key,
          userName: recipient.userName,
          petNames: [petName],
          hasRead: read,
        });
      } else {
        groups[idx].petNames.push(petName);
        groups[idx].hasRead = groups[idx].hasRead || read;
      }
    }
    return groups;
  }, [recipients]);

  const handleMediaClick = useCallback((index: number) => {
    setSelectedMediaIndex(index);
    setIsMediaViewerVisible(true);
  }, []);

  const closeMediaViewer = useCallback(() => {
    setIsMediaViewerVisible(false);
  }, []);

  function PetImageGallery({
    images,
  }: {
    images: {
      fileSeq: number;
      fileUrl: string;
      thumbnailUrl?: string | null;
      thumbnailStatus?: string | null;
    }[];
  }) {
    // 이미지가 없으면 렌더링하지 않음
    if (!images || images.length === 0) {
      return null;
    }

    if (images.length <= 3) {
      // 세로 1열, 각 이미지마다 margin
      return (
        <div className="flex flex-col gap-3">
          {images.map((src, idx) => {
            const mediaType = getMediaType(src.fileUrl || "");
            const isThumbnailReady =
              src.thumbnailStatus === "COMPLETE" && !!src.thumbnailUrl;
            const displayUrl = isThumbnailReady
              ? src.thumbnailUrl!
              : src.fileUrl;
            return (
              <div key={idx} className="relative">
                {mediaType === "video" ? (
                  <>
                    {isThumbnailReady ? (
                      <img
                        src={displayUrl}
                        alt={`video-${idx}`}
                        className="object-cover w-full rounded-xl"
                      />
                    ) : (
                      <div className="flex justify-center items-center w-full h-40 bg-gray-300 rounded-xl">
                        <svg
                          className="w-10 h-10 text-gray-500"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                        </svg>
                      </div>
                    )}
                    <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-20 rounded-xl pointer-events-none">
                      <svg
                        className="w-12 h-12 text-white opacity-80"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </>
                ) : mediaType === "document" ? (
                  <div className="h-40 w-full">
                    <DocAttachment url={src.fileUrl} />
                  </div>
                ) : (
                  <img
                    src={displayUrl || ""}
                    alt={`pet-${idx}`}
                    className="object-cover w-full rounded-xl"
                    onClick={() => handleMediaClick(idx)}
                  />
                )}
                {mediaType === "video" && (
                  <button
                    type="button"
                    className="absolute inset-0 w-full h-full"
                    onClick={() => handleMediaClick(idx)}
                    aria-label={`동영상 ${idx + 1} 보기`}
                  />
                )}
              </div>
            );
          })}
        </div>
      );
    }
    // 4개 이상: 2열 그리드로 모두 표시
    return (
      <div className="grid grid-cols-2 gap-3">
        {images.map((src, idx) => {
          const mediaType = getMediaType(src.fileUrl || "");
          const isThumbnailReady =
            src.thumbnailStatus === "COMPLETE" && !!src.thumbnailUrl;
          const displayUrl = isThumbnailReady ? src.thumbnailUrl! : src.fileUrl;
          return (
            <div key={idx} className="relative">
              {mediaType === "video" ? (
                <>
                  {isThumbnailReady ? (
                    <img
                      src={displayUrl}
                      alt={`video-${idx}`}
                      className="object-cover w-full rounded-xl h-29"
                    />
                  ) : (
                    <div className="flex justify-center items-center w-full h-29 bg-gray-300 rounded-xl">
                      <svg
                        className="w-8 h-8 text-gray-500"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                      </svg>
                    </div>
                  )}
                  <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-20 rounded-xl pointer-events-none">
                    <svg
                      className="w-10 h-10 text-white opacity-80"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </>
              ) : mediaType === "document" ? (
                <div className="h-29 w-full">
                  <DocAttachment url={src.fileUrl} />
                </div>
              ) : (
                <img
                  src={displayUrl || ""}
                  alt={`pet-${idx}`}
                  className="object-cover w-full rounded-xl h-29"
                  onClick={() => handleMediaClick(idx)}
                />
              )}
              {mediaType === "video" && (
                <button
                  type="button"
                  className="absolute inset-0 w-full h-full"
                  onClick={() => handleMediaClick(idx)}
                  aria-label={`동영상 ${idx + 1} 보기`}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <>
      <LayoutHeader
        title={
          send && send === "N"
            ? "받은 알림장"
            : send && send === "Y"
              ? "보낸 알림장"
              : "알림장"
        }
        hasBackBtn
        hasFootPrint={false}
      />
      <div className="p-2 flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5]">
        {/* 상단 프로필/타이틀 */}
        <div className="p-2">
          {isLoading ? (
            <div className="py-4 text-center">로딩 중...</div>
          ) : error ? (
            <div className="py-4 text-center text-red-500">
              에러가 발생했습니다.
            </div>
          ) : (
            <DiaryHeader
              profileImg={noticeDetail?.data?.profileImgUrl || pet1Src}
              avatarIcon={avatarIcon}
              petName={
                send === "Y" && recipients.length > 1
                  ? `${noticeDetail?.data?.petName || "또치"} 외 ${recipients.length - 1}`
                  : noticeDetail?.data?.petName || "또치"
              }
              date={
                noticeDetail?.data?.dtSend
                  ? new Date(noticeDetail.data.dtSend).toLocaleDateString()
                  : "2024.03.05"
              }
              isExistPhoto={images.length > 0}
              onDownload={() => {}}
              editedAt={editedAt}
              rightAction={
                canEdit ? (
                  <button
                    type="button"
                    onClick={handleEdit}
                    aria-label="수정"
                    title="수정"
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                    </svg>
                  </button>
                ) : undefined
              }
            />
          )}
          {/* 다중 발송 수신자 목록 — 보낸 알림장에서만 노출 */}
          {send === "Y" && recipients.length > 1 && (
            <div className="mt-2 rounded-xl bg-white p-3">
              <div className="mb-2 text-sm font-semibold">
                받는 반려견{" "}
                <span className="text-[#FF9934]">{recipients.length}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {groupedRecipients.map((group) => (
                  <span
                    key={group.key}
                    className="inline-flex items-center gap-1 rounded-full border border-[#F0F0F0] bg-[#F9F9F9] px-2.5 py-1 text-xs"
                  >
                    <span className="font-medium">
                      {group.petNames.join(", ")}
                    </span>
                    {group.userName && (
                      <span className="text-gray-400">({group.userName})</span>
                    )}
                    {group.hasRead && (
                      <span className="text-[10px] font-semibold text-[#FF9934]">
                        읽음
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
          {noticeDetail?.data.cdNotiType == "NOTI_SCHEDULE" && (
            // 달력 컴포넌트는 자체 상단 여백이 없어 위 카드(수신자 목록 등)와 붙지 않도록 간격을 준다
            <div className="mt-2">
              <CalendarS
                // label={"또치 스케줄"}
                disabled={false}
                schedules={{}}
                multiSelect={true}
                selectedDays={selectedDates}
              />
            </div>
          )}
          {/* 알림장 본문 */}
          {!isLoading && !error && (
            <NotificationCard>
              <PetImageGallery images={images} />
              <p
                className={`text-sm text-gray-500 whitespace-pre-line ${
                  images.length > 0 ? "pt-5" : ""
                }`}
              >
                {noticeDetail?.data?.notiContents || "알림 내용이 없습니다."}
              </p>
              <div>
                {/* {noticeDetail?.data?.reservationSendFlag && (
                  <button className="px-4 py-2 mt-2 rounded border border-yellow">
                    <p className="text-sm text-yellow">예약 상세 보기</p>
                  </button>
                )} */}
              </div>
            </NotificationCard>
          )}
          {noticeDetail?.data?.activities &&
            noticeDetail.data.activities.length > 0 && (
              <NotificationCard>
                <div className="grid grid-cols-3 gap-x-2 gap-y-3">
                  {noticeDetail.data.activities.map((item, index: number) => (
                    <div key={index} className="flex flex-col items-center">
                      <div className="text-sm font-semibold text-center">
                        {item.activityType}
                      </div>
                      <div className="mt-2 border rounded-lg px-6 py-2 text-[#FF9934] text-sm font-medium bg-white w-full text-center">
                        {item.activityValue}
                      </div>
                    </div>
                  ))}
                </div>
              </NotificationCard>
            )}
        </div>
        {/* 댓글 — 공지사항·일정공지 알림장에는 댓글 기능을 제공하지 않음 */}
        {!isReplyBlocked && (
        <div className="p-2">
          <div className="mb-2 text-sm font-semibold">댓글</div>
          <Divider line />
          <div className="pt-5 pb-20 space-y-4">
            {isReplyLoading ? (
              <div className="py-4 text-sm text-center text-gray-500">
                댓글을 불러오는 중...
              </div>
            ) : replyList?.data && replyList.data.length > 0 ? (
              replyList.data.map((reply, index) => (
                <CommentItem
                  key={index}
                  profileImg={
                    reply.profileImgUrl || "/images/default-profile.png"
                  }
                  name={reply.writer || "알 수 없음"}
                  content={reply.message || ""}
                  date={
                    reply.dtWrite
                      ? new Date(reply.dtWrite).toLocaleString("ko-KR", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : ""
                  }
                  // onReply={() => {
                  //   /* 답글쓰기 로직 */
                  // }}
                />
              ))
            ) : (
              <div className="py-4 text-sm text-center text-gray-500">
                아직 댓글이 없습니다.
              </div>
            )}
          </div>
          {/* 댓글 입력창 */}
          <div className="sticky bottom-14 w-full bg-[#F5F5F5] px-2 py-2 flex items-center">
            <input
              type="text"
              placeholder="댓글을 입력하세요"
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleReplySubmit();
                }
              }}
              className="flex-1 min-w-0 px-3 py-2 text-sm bg-white rounded border border-gray-300"
            />
            <button
              onClick={handleReplySubmit}
              disabled={isReplySaving || !replyMessage.trim()}
              className="flex-shrink-0 whitespace-nowrap px-4 py-2 ml-2 text-sm bg-white rounded border border-gray-300"
            >
              {isReplySaving ? "등록 중..." : "등록"}
            </button>
          </div>
        </div>
        )}
      </div>
      {/* Dialog */}
      <Dialog visible={isDialogVisible} onClose={handleDialogClose}>
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">{dialogTitle}</h3>
          <p className="mb-4 text-gray-600">{dialogMessage}</p>
          <button
            onClick={handleDialogClose}
            className="px-4 py-2 text-white bg-orange-500 rounded-lg hover:bg-orange-600"
          >
            확인
          </button>
        </div>
      </Dialog>
      {isMediaViewerVisible && mediaUrls.length > 0 && (
        <ImageViewer
          imageUrl={mediaUrls[selectedMediaIndex]}
          isVisible={isMediaViewerVisible}
          onClose={closeMediaViewer}
          onDownload={() => {}}
          images={mediaUrls}
          currentIndex={selectedMediaIndex}
          mediaType={currentMediaType}
          defaultMutedList={images.map((img) => img.muteYn === "Y")}
          onImageChange={(index) => {
            setSelectedMediaIndex(index);
          }}
        />
      )}
    </>
  );
}
